import { BillingCancellationMode } from "@redotech/crdb-enums/billing/billing-cancellation-mode";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { LineItem, ServiceInvoiceType } from "./service-invoice";

export const billingCancellationModeSchema = createEnum(
  BillingCancellationMode,
  { enumName: "BillingCancellationMode" },
);

/**
 * `fixed` products always end one way (Email/SMS bill through the paid cycle);
 * `user_choice` products let whoever cancels pick from `modes`.
 */
export type BillingCancellationPolicy = z.infer<
  typeof billingCancellationPolicySchema
>;
export const billingCancellationPolicySchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("fixed"), mode: billingCancellationModeSchema }),
  z.object({
    kind: z.literal("user_choice"),
    modes: z.array(billingCancellationModeSchema).min(1),
    defaultMode: billingCancellationModeSchema,
  }),
]);

/** Products that don't declare a policy keep the engine's original behavior. */
export const DEFAULT_BILLING_CANCELLATION_POLICY: BillingCancellationPolicy = {
  kind: "fixed",
  mode: BillingCancellationMode.EndOfPeriod,
};

export function allowedCancellationModes(
  policy: BillingCancellationPolicy,
): BillingCancellationMode[] {
  return policy.kind === "fixed" ? [policy.mode] : policy.modes;
}

export function defaultCancellationMode(
  policy: BillingCancellationPolicy,
): BillingCancellationMode {
  return policy.kind === "fixed" ? policy.mode : policy.defaultMode;
}

export type BillingCancellation = z.infer<typeof billingCancellationSchema>;
export const billingCancellationSchema = z.object({
  id: z.string(),
  productType: createEnum(ServiceInvoiceType, {
    enumName: "ServiceInvoiceType",
  }),
  mode: billingCancellationModeSchema,
  periodStart: z.coerce.date(),
  periodEnd: z.coerce.date(),
  billableUntil: z.coerce.date(),
  cancelledBy: z.string(),
  cancelledAt: z.coerce.date(),
  onCancelledHookRanAt: z.coerce.date().nullable(),
  postCancelledHookRanAt: z.coerce.date().nullable(),
});

export class BillingCancellationError extends Error {}

/**
 * When usage stops being billed for a cancellation made during the cycle
 * `[periodStart, periodEnd]`.
 */
export function resolveBillableUntil({
  mode,
  now,
  periodStart,
  periodEnd,
  nextPeriodEnd,
  customDate,
}: {
  mode: BillingCancellationMode;
  now: Date;
  periodStart: Date;
  periodEnd: Date;
  nextPeriodEnd: Date;
  customDate?: Date | null;
}): Date {
  switch (mode) {
    case BillingCancellationMode.Immediate:
      return clamp(now, periodStart, periodEnd);
    case BillingCancellationMode.EndOfPeriod:
      return periodEnd;
    case BillingCancellationMode.EndOfNextPeriod:
      return nextPeriodEnd;
    case BillingCancellationMode.Custom: {
      if (!customDate) {
        throw new BillingCancellationError("A custom date is required");
      }
      if (customDate.getTime() < periodStart.getTime()) {
        throw new BillingCancellationError(
          "The custom date can't be before the current billing period starts",
        );
      }
      return customDate;
    }
  }
}

/** The subscription closes on the cycle that contains `billableUntil`. */
export function cancellationClosesPeriod(
  cancellation: Pick<BillingCancellation, "billableUntil">,
  periodEnd: Date,
): boolean {
  return cancellation.billableUntil.getTime() <= periodEnd.getTime();
}

/** End of the usage a closing cycle bills; `undefined` bills the full cycle. */
export function usageEndsAtForPeriod(
  cancellation: Pick<BillingCancellation, "billableUntil"> | null,
  period: { start: Date; end: Date },
): Date | undefined {
  if (
    !cancellation ||
    cancellation.billableUntil.getTime() >= period.end.getTime()
  ) {
    return undefined;
  }
  return clamp(cancellation.billableUntil, period.start, period.end);
}

const UNUSED_SUBSCRIPTION_CREDIT_TYPE_HELPER = "unused-subscription-credit";

/**
 * Credits the share of a subscription fee, charged up front for `coverage`,
 * that falls after `billableUntil`. Only fees actually charged are credited.
 */
export function unusedSubscriptionCreditLineItem({
  chargedLineItems,
  coverage,
  billableUntil,
}: {
  chargedLineItems: readonly LineItem[];
  coverage: { start: Date; end: Date };
  billableUntil: Date;
}): LineItem | null {
  const fees = chargedLineItems.filter(
    (lineItem) =>
      lineItem.typeHelper === "subscription" && (lineItem.price ?? 0) > 0,
  );
  const currency = fees[0]?.currency;
  if (!currency || fees.some((fee) => fee.currency !== currency)) {
    return null;
  }
  const span = coverage.end.getTime() - coverage.start.getTime();
  if (span <= 0) {
    return null;
  }
  const unusedFraction =
    (coverage.end.getTime() -
      clamp(billableUntil, coverage.start, coverage.end).getTime()) /
    span;
  const feeCents = fees.reduce(
    (sum, fee) => sum + Math.round((fee.price ?? 0) * 100),
    0,
  );
  const creditCents = Math.floor(feeCents * unusedFraction);
  if (creditCents <= 0) {
    return null;
  }
  return {
    name: "Unused subscription credit",
    nameDescription:
      "Billing was cancelled mid-period; the fee for the rest of the period is credited.",
    price: -creditCents / 100,
    currency,
    quantity: 1,
    typeHelper: UNUSED_SUBSCRIPTION_CREDIT_TYPE_HELPER,
  };
}

/**
 * Payment rails can't charge a negative total, so the credit shrinks to the
 * bill's total; `uncredited` is what still needs refunding by hand.
 */
export function capUnusedSubscriptionCredit(lineItems: LineItem[]): {
  lineItems: LineItem[];
  uncredited: number;
} {
  const totalCents = lineItems.reduce(
    (sum, lineItem) => sum + Math.round((lineItem.price ?? 0) * 100),
    0,
  );
  if (totalCents >= 0) {
    return { lineItems, uncredited: 0 };
  }
  let overCents = -totalCents;
  const capped: LineItem[] = [];
  for (const lineItem of lineItems) {
    if (
      overCents > 0 &&
      lineItem.typeHelper === UNUSED_SUBSCRIPTION_CREDIT_TYPE_HELPER &&
      lineItem.price !== null
    ) {
      const creditCents = -Math.round(lineItem.price * 100);
      const reducedBy = Math.min(creditCents, overCents);
      overCents -= reducedBy;
      if (creditCents > reducedBy) {
        capped.push({ ...lineItem, price: -(creditCents - reducedBy) / 100 });
      }
      continue;
    }
    capped.push(lineItem);
  }
  return { lineItems: capped, uncredited: (-totalCents - overCents) / 100 };
}

function clamp(date: Date, min: Date, max: Date): Date {
  return new Date(
    Math.min(Math.max(date.getTime(), min.getTime()), max.getTime()),
  );
}
